import './styles.scoped.css'
import {Typography, Button, IconButton, Modal, Avatar, Chip} from '@mui/material'
import {Close, DateRange, AccessTime} from '@mui/icons-material'
import {useMobile} from '../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../utils/helpers'
import {format} from 'date-fns'
import {scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {SkeletonContainer, TextSkeleton} from '../Skeletons'

export default ({open, setOpen, loadData, items = [], loading = false}) => {
    const {mobile} = useMobile()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()

    const submit = () => {
        setLoading(true)
        scheduleApi
            .sendSignatureReminder(branchId, items?.[0]?.schedule_id, {visit_ids: items?.map(item => item?.id)})
            .then(() => {
                setLoading(false)
                setOpen(false)
                if (items?.length == 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Reminder Sent!',
                        description: `${items?.[0]?.schedule?.caregiver?.first_name} has been reminded to submit their signature for the visit on ${format(new Date(items?.[0]?.clock_in_time ?? null), 'EEE, dd MMM yyyy')} at ${format(new Date(items?.[0]?.clock_in_time ?? null), 'h:mm a')}.`,
                    })
                } else if (items?.length > 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Reminder Sent!',
                        description: `Reminders have been successfully sent to ${new Set(items?.map(item => item?.schedule?.client?.id)).size} clients to submit their signatures for ${items?.length} completed visits.`,
                    })
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Send Signature Reminder to{items?.length > 1 ? ' ' + items?.length : ''} Caregiver{items?.length > 1 ? 's' : ''}?
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={submit} disabled={loading} style={{flex: 1}}>
                                    {items?.length > 1 ? `Send Reminders (${items?.length})` : 'Send Reminder'}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Send Signature Reminder to{items?.length > 1 ? ' ' + items?.length : ''} Caregiver{items?.length > 1 ? 's' : ''}?
                        </Typography>
                    )}
                    <br />
                    {loading ? (
                        <SkeletonContainer>
                            <TextSkeleton width="100%" fontSize={14} sx={{mx: 'auto'}} />
                            <TextSkeleton width="65%" fontSize={14} sx={{mx: mobile ? 0 : 'auto'}} />
                        </SkeletonContainer>
                    ) : (
                        <Typography variant="body2" color="text.primary" className="title">
                            {items?.length > 1
                                ? `You are about to send reminders to ${new Set(items?.map(item => item?.schedule?.client?.id)).size} clients for ${items?.length} completed visits that require their signatures.`
                                : `You are about to send a reminder to ${items?.[0]?.schedule?.caregiver?.first_name} to submit their signature for the visit on ${format(new Date(items?.[0]?.clock_in_time ?? null), 'EEE, dd MMM yyyy')} at ${format(new Date(items?.[0]?.clock_in_time ?? null), 'h:mm a')}. Would you like to proceed?`}
                        </Typography>
                    )}
                    {items?.length > 1 &&
                        items?.map((item, index) => (
                            <div key={index}>
                                <br />
                                <div className="card">
                                    <div className="card-profile">
                                        <div className="people-card">
                                            {item?.schedule?.client?.avatar_url ? (
                                                <Avatar src={item?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(
                                                            item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name,
                                                        ),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.schedule?.service?.name}
                                                </Typography>
                                            </div>
                                        </div>
                                        {item?.status === 'scheduled' && (
                                            <Chip
                                                label="Scheduled"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: theme.palette.grey[700],
                                                    color: theme.palette.secondary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'in_progress' && <Chip label="In progress" variant="filled" color="warning" size="small" />}
                                        {item?.status === 'ended' && (
                                            <Chip
                                                label="Ended"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: '#02CAD1',
                                                    color: theme.palette.primary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" size="small" />}
                                        {item?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" size="small" />}
                                        {item?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" size="small" />}
                                    </div>
                                    <br />
                                    <div className="datetime">
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <DateRange fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                            </Typography>
                                        </div>
                                        <Typography variant="body3" color="text.secondary">
                                            |
                                        </Typography>
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <AccessTime fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={submit} disabled={loading}>
                                {items?.length > 1 ? `Send Reminders (${items?.length})` : 'Send Reminder'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
